import { useCallback, useEffect, useState } from "react";
import { useHttp } from "../../hooks";
import {
	AlertType,
	Clinic,
	DoctorUser,
	GetDoctorUsersResponse
} from "../../models";
import { Alert, Box, CircularProgress } from "@mui/material";
import {
	useAlertContext,
	useClinicContext,
	useDoctorContext,
	useUserContext
} from "../../contexts";
import { BuildUrl, mapDoctor } from "../../helpers";
import { useLocation } from "react-router-dom";
import { DoctorsFilter } from "./DoctorsFilter";
import { DoctorsTable } from "./DoctorsTable";
import { useMsal } from "@azure/msal-react";
import { apiAccessRequest } from "../../constants";

interface props {
	clinic: Clinic | undefined;
	setClinic: React.Dispatch<React.SetStateAction<Clinic | undefined>>;
}

export const Doctors = ({ clinic, setClinic }: props) => {
	const { showAlert } = useAlertContext();
	const { user } = useUserContext();
	const { setUsers, refreshUsers, setRefreshUsers } = useDoctorContext();
	const { clinics } = useClinicContext();
	const location = useLocation();
	const { account } = useUserContext();
	const { instance } = useMsal();
	const consultsUrl = process.env.REACT_APP_CONSULTS_URL;

	const refreshRequired = location.state?.refreshRequired as boolean;
	const [refreshData, setRefreshData] = useState(refreshRequired);
	const [filteredDoctors, setFilteredDoctors] = useState<DoctorUser[]>([]);
	const clinicFilteredDoctors = clinic
		? filteredDoctors.filter((user) =>
				user.doctor.bpDoctors?.some((doctor) => doctor.clinicId === clinic.id)
			)
		: filteredDoctors;

	const {
		data: getDoctorsResponse,
		isLoading,
		isError,
		fetchData: getDoctors
	} = useHttp<GetDoctorUsersResponse>(`${consultsUrl}User`, {
		method: "GET"
	});

	useEffect(() => {
		if (user || refreshData || refreshUsers) {
			getDoctors();
			if (refreshData) {
				setRefreshData(false);
			}
			if (refreshUsers) {
				setRefreshUsers(false);
			}
			setTimeout(() => {
				window.scrollTo(0, 0);
			}, 500);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, refreshUsers]);

	useEffect(() => {
		if (isError) {
			showAlert({
				message: "There was an error retrieving doctors",
				alertType: AlertType.error
			});
			return;
		}

		if (isLoading) {
			return;
		}

		if (getDoctorsResponse) {
			const users = getDoctorsResponse.value.map((user) => ({
				...user,
				doctor: mapDoctor(user.doctor)
			}));
			setUsers(users);
			setFilteredDoctors(users);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, isError, getDoctorsResponse]);

	const handleImportDoctorsClick = useCallback(async () => {
		try {
			const requestUrl = BuildUrl(`User/sync`);
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				...(tokenResponse && {
					Authorization: `Bearer ${tokenResponse.accessToken}`
				}),
				...{ "Content-Type": "application/json" }
			};
			const response = await fetch(requestUrl, {
				method: "POST",
				headers
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}

			setRefreshUsers(true);
		} catch (error) {
			console.info(error);
			showAlert({
				message: "Error fetching Doctors",
				alertType: AlertType.error
			});
		}
	}, [account, instance, setRefreshUsers, showAlert]);

	const handleImportDoctorSessionsClick = useCallback(async () => {
		try {
			const requestUrl = BuildUrl(`Doctor/sessions/sync`);
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				...(tokenResponse && {
					Authorization: `Bearer ${tokenResponse.accessToken}`
				}),
				...{ "Content-Type": "application/json" }
			};
			const response = await fetch(requestUrl, {
				method: "POST",
				headers
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}
			showAlert({
				message:
					"Doctor Sessions import started. It may take 5 minutes to finish",
				alertType: AlertType.success
			});
		} catch (error) {
			console.info(error);
			showAlert({
				message: "Error importing Doctor Sessions",
				alertType: AlertType.error
			});
		}
	}, [account, instance, showAlert]);

	if (isLoading) {
		return (
			<Box className="icon-loading">
				<CircularProgress size={70} />
			</Box>
		);
	} else if (isError) {
		return <></>;
	} else {
		return (
			<>
				<div className="flex justify-between mb-2">
					<h2>Doctors</h2>
					<div className="flex gap-4">
						<button
							className="ch-button ch-button-primary ch-button-small"
							onClick={() => handleImportDoctorsClick()}
						>
							<img src="images/svg/file-upload.svg" alt="Import Doctors" />
							Import Doctors
						</button>
						<button
							className="ch-button ch-button-primary ch-button-small"
							onClick={() => handleImportDoctorSessionsClick()}
						>
							<img
								src="images/svg/calendar-upload.svg"
								alt="Import Doctor Sessions"
							/>
							Import Doctor Sessions
						</button>
					</div>
				</div>
				{/* <div className="flex flex-row gap-2 mb-6"> */}
				<DoctorsFilter
					clinic={clinic}
					setClinic={setClinic}
					setFilteredDoctors={setFilteredDoctors}
				/>
				{/* </div> */}

				{clinicFilteredDoctors.length === 0 && (
					<Alert severity="info">There are no matching doctors</Alert>
				)}
				{clinicFilteredDoctors.length > 0 && (
					<DoctorsTable
						filteredDoctors={clinicFilteredDoctors}
						clinics={clinics ?? []}
						clinic={clinic}
					/>
				)}
			</>
		);
	}
};
